import ContractAttachment from '@/components/ContractAttachment'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getNegotiationHistory } from '@/modules/Customer/services'
import { NegotiationHistory } from '@/modules/Customer/types'
import { formatDateWithFormatter, randomAvatarSrc } from '@/utils/function'
import useAuthStore from '@/stores/useAuthStore'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Avatar, Chip, Typogaphy } from 'vnetwork-ui'

interface HistoryProps {
    negotiationID: number
}

/** Action type → chip color */
const ACTION_COLORS: Record<string, string> = {
    resolve: 'success',
    reject: 'error',
    cancel: 'default',
    status_update: 'info',
    note: 'warning',
}

const History: React.FC<HistoryProps> = ({ negotiationID }) => {
    const { t } = useTranslation()
    const { employee } = useAuthStore()

    const { data: historyData, renderState } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.NEGOTIATIONS, 'history', negotiationID],
            queryFn: () => getNegotiationHistory(negotiationID),
        },
        { showError: false }
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const entries = historyData?.results || []

    return (
        <div>
            <Typogaphy size="small" className="font-medium text-gray-700 mb-3">
                {t('negotiation-history')}
                {entries.length > 0 && (
                    <span className="text-gray-400 font-normal ml-1">
                        ({entries.length})
                    </span>
                )}
            </Typogaphy>

            {entries.length === 0 ? (
                <div className="h-16 flex justify-center items-center">
                    <Typogaphy className="text-gray-400 text-sm">
                        {t('no-history-available')}
                    </Typogaphy>
                </div>
            ) : (
                <div className="space-y-3">
                    {entries.map((entry: NegotiationHistory) => {
                        const isSelf =
                            !!employee?.employee_id &&
                            entry.action_by === employee.employee_id

                        return (
                            <div
                                key={entry.contract_negotiation_history_id}
                                className={
                                    isSelf
                                        ? 'flex justify-end'
                                        : 'flex justify-start'
                                }
                            >
                                <div
                                    className={
                                        isSelf
                                            ? 'flex flex-row-reverse items-start gap-2 max-w-[85%]'
                                            : 'flex items-start gap-2 max-w-[85%]'
                                    }
                                >
                                    <Avatar
                                        size="small"
                                        src={randomAvatarSrc(
                                            entry.action_by_name
                                        )}
                                    />
                                    <div className="min-w-0">
                                        <div
                                            className={
                                                isSelf
                                                    ? 'rounded-2xl rounded-tr-sm border border-blue-200 bg-blue-50 px-3 py-2.5'
                                                    : 'rounded-2xl rounded-tl-sm border border-gray-200 bg-white px-3 py-2.5'
                                            }
                                        >
                                            {/* Header: name + action chip */}
                                            <div className="flex items-center justify-between gap-2">
                                                <Typogaphy className="text-sm font-medium text-gray-800 truncate">
                                                    {entry.action_by_name ||
                                                        '---'}
                                                </Typogaphy>
                                                <Chip
                                                    label={t(
                                                        `history_type.${entry.action_type}`
                                                    )}
                                                    color={
                                                        (ACTION_COLORS[
                                                            entry.action_type
                                                        ] || 'default') as any
                                                    }
                                                    size="small"
                                                />
                                            </div>

                                            {/* Timestamp */}
                                            <Typogaphy className="text-[11px] text-gray-500 mt-0.5">
                                                {formatDateWithFormatter(
                                                    entry.created_at,
                                                    'DD-MM-YYYY HH:mm'
                                                )}
                                            </Typogaphy>

                                            {/* Status change */}
                                            {entry.action_type ===
                                                'status_update' &&
                                                entry.previous_status &&
                                                entry.new_status && (
                                                    <Typogaphy className="text-xs text-gray-600 mt-1.5">
                                                        {t(
                                                            'status-changed-from-to',
                                                            {
                                                                from: entry.previous_status,
                                                                to: entry.new_status,
                                                            }
                                                        )}
                                                    </Typogaphy>
                                                )}

                                            {/* Notes */}
                                            {entry.action_notes && (
                                                <div className="mt-2">
                                                    <Typogaphy className="text-sm text-gray-800 whitespace-pre-wrap">
                                                        {entry.action_notes}
                                                    </Typogaphy>
                                                </div>
                                            )}

                                            {/* Reason (reject/cancel) */}
                                            {entry.action_reason && (
                                                <div className="mt-2 bg-amber-50 border border-amber-100 rounded-lg p-2">
                                                    <Typogaphy className="text-xs text-amber-700">
                                                        {t('reason')}:{' '}
                                                        {entry.action_reason}
                                                    </Typogaphy>
                                                </div>
                                            )}

                                            {/* Attachments */}
                                            {entry.attachments?.length > 0 && (
                                                <div className="mt-2">
                                                    <ContractAttachment
                                                        attachments={
                                                            entry.attachments
                                                        }
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

export default History
